import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import usePushNotifications from "@/hooks/usePushNotifications";
import { Bell, BellOff, X, Check, Sparkles } from "lucide-react";

const DISMISS_KEY = "talabya_push_dismissed_v1";

export default function PushEnableBanner() {
  const { user } = useAuth();
  const push = usePushNotifications({ userId: user?.id });
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
  }, [user?.id]);

  if (!user) return null;
  if (!push.supported) return null;
  if (push.subscribed) return null;
  if (dismissed) return null;
  if (push.permission === "denied") return null;

  const doEnable = async () => {
    const r = await push.enable();
    if (r.ok) {
      setTesting(true);
      await push.sendTest();
      setTimeout(() => setTesting(false), 2000);
    }
  };

  const doDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const roleText = user.role === "driver"
    ? "استلم تنبيهات الطلبات الجديدة الجاهزة فور وصولها"
    : user.role === "admin"
    ? "استلم تنبيهات المهام والطلبات الجديدة فوراً"
    : "تابع حالة طلبك لحظياً حتى إذا أغلقت التطبيق";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4" data-testid="push-banner">
      <div
        className="relative rounded-2xl overflow-hidden animate-fade-up"
        style={{
          background: "#FFFFFF",
          border: "1px solid var(--border)",
          boxShadow: "0 6px 24px rgba(37,71,208,0.10), 0 1px 2px rgba(44,40,37,0.04)",
        }}
      >
        {/* Left accent stripe */}
        <div
          className="absolute inset-y-0 right-0 w-1.5"
          style={{ background: "linear-gradient(180deg, #E05A3D 0%, #C5452A 100%)" }}
        />

        {/* Soft cream highlight */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(120% 100% at 100% 0%, rgba(37,71,208,0.06) 0%, transparent 60%)",
          }}
        />

        <div className="relative flex items-start gap-3 p-4 pr-5">
          {/* Animated bell icon */}
          <div className="shrink-0">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center relative"
              style={{
                background: "linear-gradient(135deg, #FDECE8 0%, #FBD9D0 100%)",
                border: "1px solid rgba(37,71,208,0.15)",
              }}
            >
              <Bell size={22} className="text-primary-brand animate-pulse-dot" strokeWidth={2.5} />
              <span
                className="absolute -top-1 -left-1 w-3 h-3 rounded-full bg-primary-brand ring-2 ring-white animate-pulse"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className="font-black text-base sm:text-lg text-[var(--text-primary)]"
                style={{ fontFamily: "Cairo" }}
              >
                فعّل إشعارات طلبية
              </span>
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: "#FDECE8", color: "#C5452A" }}
              >
                <Sparkles size={10} /> موصى به
              </span>
            </div>

            <div className="text-[13px] sm:text-sm text-[var(--text-secondary)] mt-1 leading-relaxed">
              {roleText}
            </div>

            {push.error && (
              <div
                className="text-[11px] mt-2 inline-block px-2 py-1 rounded-lg font-bold"
                style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" }}
              >
                {push.error}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={doEnable}
                disabled={push.busy || testing}
                className="px-4 py-2 rounded-xl font-black text-sm text-white flex items-center gap-1.5 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: testing
                    ? "#16A34A"
                    : "linear-gradient(135deg, #E05A3D 0%, #C5452A 100%)",
                  boxShadow: testing
                    ? "0 4px 14px rgba(22,163,74,0.35)"
                    : "0 4px 14px rgba(37,71,208,0.35)",
                }}
                data-testid="push-enable-btn"
              >
                {testing ? (
                  <>
                    <Check size={15} strokeWidth={3} /> تم! تحقق من الإشعار
                  </>
                ) : push.busy ? (
                  <>جاري التفعيل...</>
                ) : (
                  <>
                    <Bell size={15} strokeWidth={2.5} /> تفعيل الإشعارات
                  </>
                )}
              </button>

              <button
                onClick={doDismiss}
                className="px-3 py-2 rounded-xl text-sm font-bold transition-colors"
                style={{
                  background: "var(--surface-alt)",
                  color: "var(--text-secondary)",
                }}
                data-testid="push-dismiss-btn"
              >
                لاحقاً
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={doDismiss}
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors hover:bg-[var(--surface-alt)]"
            style={{ color: "var(--text-secondary)" }}
            aria-label="إغلاق"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Small toggle for settings
export function PushToggle() {
  const { user } = useAuth();
  const push = usePushNotifications({ userId: user?.id });
  if (!user || !push.supported) return null;
  return (
    <button
      onClick={() => (push.subscribed ? push.disable() : push.enable())}
      disabled={push.busy}
      className={`text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${
        push.subscribed
          ? "bg-green-100 text-green-700 hover:bg-green-200"
          : "bg-[var(--surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--border)]"
      }`}
      data-testid="push-toggle"
    >
      {push.subscribed ? (
        <>
          <Bell size={12} /> إشعارات مفعّلة
        </>
      ) : (
        <>
          <BellOff size={12} /> فعّل الإشعارات
        </>
      )}
    </button>
  );
}
